<template>
  <div>
    <b-row>
      <b-col
        cols="12"
        md="12"
      >
        <div class="d-flex align-items-center mt-1 justify-content-between">
          <h5>{{ $t('edit-community-modal.subscription.transactions.title') }}</h5>
          <b-form-group class="mb-0">
            <flat-pickr
              :value="searchDateRange"
              class="form-control"
              :config="{ mode: 'range', showMonths: 2, dateFormat: 'Y-m-d' }"
              @on-close="updateDateRange"
            />
          </b-form-group>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <b-table
          striped
          hover
          responsive
          :items="transactions"
          :fields="fields"
          :busy="isLoading"
          :empty-text="$t('edit-community-modal.subscription.transaction-table.no-transaction')"
          show-empty
          class="mt-1"
        >
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle" />
            </div>
          </template>

          <template #cell(date)="data">
            <span v-b-tooltip.hover="moment(data.item.payout_date).fromNow()">{{ moment(data.item.payout_date).format('YYYY-MM-DD') }}</span>
          </template>

          <template #cell(amount)="data">
            <span>{{ data.item.currency.slice(0, 2) }}$ {{ data.item.amount }}</span>
          </template>

          <template #cell(status)="data">
            <b-badge :variant="data.item.is_paid ? 'success' : 'warning'">
              {{ $t(`edit-community-modal.subscription.transaction-table.status-${data.item.is_paid ? 'paid' : 'pending'}`) }}
            </b-badge>
          </template>

          <template #cell(receipt)="data">
            <a
              :href="data.item.receipt_url"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ $t('edit-community-modal.subscription.transaction-table.receipt') }}
            </a>
          </template>
        </b-table>
      </b-col>

      <b-col cols="12">
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center">
            <span class="text-nowrap">
              {{ textPagination }}
            </span>
          </div>
          <div>
            <b-pagination
              v-model="rowsMetadata.pageNumber"
              :total-rows="rowsMetadata.totalRecords"
              :per-page="rowsMetadata.maxPageSize"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="my-0"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { BRow, BCol, BFormGroup, BTable, BPagination, BSpinner, BBadge, VBTooltip } from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'

export default {
  name: 'TransactionsHistory',
  components: {
    BRow,
    BCol,
    BFormGroup,
    BTable,
    BPagination,
    BSpinner,
    BBadge,

    flatPickr,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      isLoading: false,
      rowsMetadata: {
        pageNumber: 1,
        pageSize: 10,
        maxPageSize: 10,
        totalRecords: 0,
        totalPages: 0,
      },
      searchDateRange: '',
      defaultDateRange: [],
      transactions: [],
      fields: [
        {
          key: 'id',
          label: this.$t('edit-community-modal.subscription.transaction-table.id'),
          sortable: true,
        },
        {
          key: 'date',
          label: this.$t('edit-community-modal.subscription.transaction-table.date'),
          sortable: true,
        },
        {
          key: 'amount',
          label: this.$t('edit-community-modal.subscription.transaction-table.amount'),
          sortable: true,
        },
        {
          key: 'status',
          label: this.$t('edit-community-modal.subscription.transaction-table.status'),
          sortable: true,
        },
        {
          key: 'receipt',
          label: this.$t('edit-community-modal.subscription.transaction-table.receipt'),
          sortable: false,
        },
      ],
    }
  },
  computed: {
    textPagination() {
      const { pageNumber, pageSize, totalRecords } = this.rowsMetadata
      const start = Math.min((pageNumber - 1) * pageSize + 1, totalRecords)
      const end = Math.min(pageNumber * pageSize, totalRecords)

      return this.$t('edit-community-modal.services.table.pagination-information', {
        firstIndex: start,
        lastIndex: end,
        totalRecords,
      })
    },
  },
  watch: {
    'rowsMetadata.pageNumber': {
      handler() {
        this.fetchTransactions(this.rowsMetadata.pageNumber)
      },
    },
  },
  mounted() {
    this.defaultDateRange = [this.moment().subtract(1, 'month').format(), this.moment().format()]
    this.searchDateRange = this.defaultDateRange

    this.fetchTransactions()
  },
  methods: {
    updateDateRange(dateRange) {
      this.searchDateRange = [this.moment(dateRange[0]).format(), this.moment(dateRange[1]).format()]

      this.fetchTransactions()
    },
    // eslint-disable-next-line no-unused-vars
    fetchTransactions(pageNumber = 1) {
      this.isLoading = true

      this.$store
        .dispatch('admin/FETCH_SUBSCRIPTION_TRANSACTIONS', {
          pageNumber,
          pageSize: this.rowsMetadata.maxPageSize,
          startDate: this.searchDateRange[0],
          endDate: this.searchDateRange[1],
        })
        .then(response => {
          this.transactions = response.data.items

          this.rowsMetadata.pageSize = response.data.pageSize
          this.rowsMetadata.totalRecords = response.data.totalRecords
          this.rowsMetadata.totalPages = response.data.totalPages
        })
        .catch(error => {
          this.onError(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    onError(error) {
      this.$emit('error', error)
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
